import React from 'react';
import { Reveal } from '../components/Reveal';
import { FileSignature, ClipboardCheck, Repeat, Lock } from 'lucide-react';

/**
 * Marketing ruling M1 (2026-10-20), Phase A. Step 4 is the only place the
 * page mentions live trading, and it says it is not open. The Phase C
 * variant ("Live trading is open for admitted rules on Managed accounts.
 * Paper first is still required.") is counsel-gated and lands behind
 * LANDING_LIVE_AVAILABLE, not here. The old "Mandate-Bounded Proposals"
 * highlight is merged into step 2.
 */
const steps = [
  {
    icon: FileSignature,
    title: 'Set your limits.',
    description:
      'You sign a mandate: the most you will hold in one position, the most you will lose in a day, the drawdown at which the portfolio shrinks and then stops, what it may hold, and what the agent may never do: raise a limit, restart after a halt, touch your broker credentials, or withdraw. We call this document a mandate. Only you can change it.',
    open: true,
  },
  {
    icon: ClipboardCheck,
    title: 'Review the proposal.',
    description:
      'The agent proposes a portfolio from our library of documented rules, sized to fit inside your limits, which only you can change. You see what it holds, why, and which limit permits it. You approve or reject. Nothing runs without your approval.',
    open: true,
  },
  {
    icon: Repeat,
    title: 'It runs on paper.',
    description:
      'Deterministic code, not the AI, rebalances a paper account at your broker on a monthly schedule, checks every order against your limits before sending it, and records it. If an order would cross a limit, it is refused and you are told.',
    open: true,
  },
  {
    icon: Lock,
    title: 'Live, later.',
    description:
      'Not available today. Before any account trades real money: the rule must be in our admitted tier; its paper record must show real trading costs inside the range we modelled; and our terms must be reviewed by counsel. We will change this sentence when that is true.',
    open: false,
  },
];

/**
 * The customer's whole journey in five plain words each, in the order they
 * do it (design audit R58, PR group 5). The four cards below say what the
 * system does; this strip says what the visitor does.
 */
export const JOURNEY_STEPS = [
  'Connect your broker (a paper account)',
  'Set your limits',
  'Review a proposal',
  'Approve it',
  'Watch it',
] as const;

export const HowItWorks: React.FC = () => {
  return (
    <section id="how-it-works" className="py-28 px-6 scroll-mt-20">
      <div className="max-w-[1100px] mx-auto">
        <Reveal
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm text-[#38bdf8] font-medium uppercase tracking-widest mb-4">
            How it works
          </p>
          <h2 className="font-heading text-3xl md:text-[2.75rem] font-bold text-white leading-tight">
            Four steps.{' '}
            <span className="text-gradient">The last one is not open yet.</span>
          </h2>
        </Reveal>

        <ol
          data-testid="journey-strip"
          aria-label="Your path from sign-up to a running paper plan"
          className="mb-16 flex flex-wrap justify-center gap-3"
        >
          {JOURNEY_STEPS.map((label, i) => (
            <li
              key={label}
              className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-white/70"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#38bdf8]/15 text-xs font-semibold text-[#38bdf8]">
                {i + 1}
              </span>
              {label}
            </li>
          ))}
        </ol>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-0">
          {steps.map((step, i) => (
            <Reveal
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className={`relative flex flex-col items-center text-center px-6 ${step.open ? '' : 'opacity-70'}`}
            >
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(50%+28px)] right-[calc(-50%+28px)] h-px bg-gradient-to-r from-white/10 to-white/5" />
              )}

              {/* Step number + icon */}
              <div className="relative w-16 h-16 rounded-2xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-6 text-white/60">
                <step.icon size={24} />
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[hsl(225,20%,4%)] border border-white/10 flex items-center justify-center text-xs text-white/40 font-medium">
                  {i + 1}
                </span>
              </div>

              <h3 className="font-heading text-lg font-semibold text-white mb-2">
                {step.title}
              </h3>
              {!step.open && (
                <span className="mb-3 inline-flex items-center rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-0.5 text-xs font-semibold uppercase tracking-wider text-amber-300">
                  Not open
                </span>
              )}
              <p className="text-white/50 text-sm leading-relaxed">
                {step.description}
              </p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
